<script lang="ts" setup>
import { computed } from 'vue';

import VDialog from '@components/dialogs/VDialog/VDialog.vue';
import VMessage from '@components/message/VMessage/VMessage.vue';
import { useI18n } from '@modules/i18n/composables';
import { useCurrentNamespace } from '@modules/permissions/composables';
import {
  TRANSLATION_GROUP_ADMIN_WEBHOOKS_DETAILS,
  TRANSLATION_GROUP_AFFILIATES_WEBHOOKS_DETAILS,
} from '@modules/webhooks-history/submodules/history-details/constants';

import type {
  IWebhooksHistoryRequestDetailsDialogEmits,
  IWebhooksHistoryRequestDetailsDialogProps,
} from './types';

const props = defineProps<IWebhooksHistoryRequestDetailsDialogProps>();

const emit = defineEmits<IWebhooksHistoryRequestDetailsDialogEmits>();

const visible = defineModel<boolean>('visible');

const { isAdminNamespace } = useCurrentNamespace();
const { getTranslation } = useI18n();

const translationGroup = computed(() => (isAdminNamespace.value
  ? TRANSLATION_GROUP_ADMIN_WEBHOOKS_DETAILS
  : TRANSLATION_GROUP_AFFILIATES_WEBHOOKS_DETAILS
));

const severity = computed(() => (props.data?.execution_status === 'completed_without_errors' ? 'success' : 'error'));

const executionStatus = computed(() => getTranslation(translationGroup.value, `REQUEST_DETAILS_STATUS.${props.data?.execution_status}`, { defaultValue: props.data?.execution_status }));

function onHide() {
  visible.value = false;
}

function onAfterHide() {
  emit('after-hide');
}
</script>

<template>
  <VDialog
    :visible="visible"
    :title="$getTranslation(translationGroup, 'REQUEST_DETAILS_HEADER')"
    @hide="onHide"
    @after-hide="onAfterHide"
  >
    <div class="max-w-[650px]">
      <PPanel
        v-if="data?.execution_status"
        :header="$getTranslation(translationGroup, 'REQUEST_DETAILS_EXECUTION_STATUS_TITLE')"
        class="mb-4"
      >
        <VMessage :severity="severity">
          {{ executionStatus }}
        </VMessage>
      </PPanel>
      <PPanel
        v-if="data?.explanations?.messages?.length"
        :header="$getTranslation(translationGroup, 'REQUEST_DETAILS_ERROR_MESSAGES_TITLE')"
      >
        <VMessage
          v-for="message in data?.explanations?.messages"
          :key="message"
          :severity="severity"
          class="mb-3"
        >
          {{ $t(message) }}
        </VMessage>
      </PPanel>
    </div>
  </VDialog>
</template>
